/**
 * Registry of `/learn/*` articles — the informational content layer.
 *
 * WHY THIS SECTION EXISTS. The free tools rank for *transactional* queries ("dkim checker") and
 * that surface is already built out. What the site had none of was the *informational* half of the
 * same intent — "what is a DKIM selector", "spf too many dns lookups", "why does my email go to
 * spam". Those queries are lower competition, they are what AI Overviews and assistants lift
 * answers from, and each one has an obvious next click into a tool we already own.
 *
 * WHY `/learn/` AND NOT `/glossary/`. "Glossary" sets an expectation of one-paragraph definitions.
 * These are 1,200+ word explainers and will get longer, not shorter.
 *
 * HUB AND SPOKE. Each article belongs to exactly one CLUSTER, and each cluster has exactly one
 * PILLAR. The link matrix (spoke → pillar, pillar → every spoke, spoke ↔ 2-3 siblings) is DERIVED
 * from this file by `LearnPageLayout`, not hand-maintained per page — hand-maintained link matrices
 * rot on the third article and nobody notices until a page is orphaned. `learn-clusters.spec.ts`
 * asserts the derived matrix actually holds in the built HTML.
 *
 * ONE PAGE = ONE INTENT is the rule that stops this cannibalising `/tools/*` and `/features/*`.
 * See docs/CONTENT-PLAN.md §1, and the near-duplicate guard in test/seo.spec.ts that enforces it.
 *
 * Adding an article: an entry here + a page module + a route in `routes.ts` + an entry in
 * `scripts/public-routes.mjs` + `STATIC_ROUTES`/`LEARN_LLMS` in the API's public-seo controller.
 * The last three are pinned by tests; the first two are not, because they cannot build without you.
 */

export type ClusterKey = 'authentication' | 'deliverability' | 'integrity' | 'copy';

export interface LearnCluster {
  key: ClusterKey;
  /** Human label used in the index grouping and in the "part of" line on each article. */
  name: string;
  /** One line explaining what the cluster covers — rendered on the index. */
  summary: string;
}

export const LEARN_CLUSTERS: readonly LearnCluster[] = [
  {
    key: 'authentication',
    name: 'Email authentication',
    summary:
      'SPF, DKIM and DMARC — what each record proves, where it lives, and which one is failing when mail gets refused.',
  },
  {
    key: 'deliverability',
    name: 'Deliverability and reputation',
    summary:
      'Why mail that authenticates correctly still lands in spam: blacklists, sender reputation, SMTP refusals, warmup.',
  },
  {
    key: 'integrity',
    name: 'List quality and signup integrity',
    summary:
      'The addresses that should never have entered your list, and the signups that were never a person.',
  },
  {
    key: 'copy',
    name: 'Copy and content',
    summary: 'What the message itself does to your delivery odds — subject lines and spam triggers.',
  },
];

export interface LearnArticleMeta {
  /** URL slug under /learn/<slug>. */
  slug: string;
  /** H1 + <title>, phrased as the question people actually type. */
  title: string;
  /** The term being defined — feeds DefinedTerm.name. Not the same as the title. */
  term: string;
  /** Meta description. ≤ 160 chars (the SERP contract asserts this). */
  description: string;
  /** One-line blurb on the index card and in sibling rails. Distinct from `description`. */
  blurb: string;
  /** Which cluster this belongs to. */
  cluster: ClusterKey;
  /** Exactly one article per cluster carries this — the pillar every spoke links up to. */
  pillar?: true;
  /**
   * Date the article was last substantively reviewed, YYYY-MM-DD. Rendered visibly AND emitted as
   * `dateModified`. Bump it when you change the FACTS, not when you fix a typo — a date that moves
   * for cosmetic edits is the "content churn for freshness signals" pattern Google names explicitly.
   */
  updated: string;
  /** The tool this article converts into — the article's primary CTA. */
  tool: { to: string; label: string };
  /** The commercial page this article funnels toward, when one applies. Optional by design: */
  feature?: { to: string; label: string };
  /**
   * Extra tool/feature pages this article is genuinely relevant to, beyond its primary CTA.
   * Only for real matches — `/learn/spf-record/` explains the record BOTH the SPF checker and the
   * SPF generator produce. Do not pad this to manufacture links: a link to a loosely-related page
   * teaches the wrong association, which is worse than no link at all.
   */
  alsoServes?: readonly string[];
  /**
   * Primary sources this article rests on — RFCs, provider documentation, standards bodies.
   *
   * Rendered as a visible Sources list AND emitted as `TechArticle.citation`. This is an E-E-A-T
   * and GEO signal, not decoration: "citations to primary sources" is one of the authority signals
   * AI-search systems weigh when deciding what to cite, and an explainer about a protocol that
   * never points at the protocol is asking to be taken on faith.
   *
   * Only genuine primary sources. A link to another blog post is not a citation.
   */
  citations?: ReadonlyArray<{ title: string; url: string }>;
}

/** Primary sources, declared once so the same RFC is cited identically wherever it appears. */
export const SOURCE = {
  rfc7208: { title: 'RFC 7208 — Sender Policy Framework (SPF)', url: 'https://www.rfc-editor.org/rfc/rfc7208' },
  rfc6376: { title: 'RFC 6376 — DomainKeys Identified Mail (DKIM)', url: 'https://www.rfc-editor.org/rfc/rfc6376' },
  rfc7489: { title: 'RFC 7489 — DMARC', url: 'https://www.rfc-editor.org/rfc/rfc7489' },
  rfc5321: { title: 'RFC 5321 — Simple Mail Transfer Protocol', url: 'https://www.rfc-editor.org/rfc/rfc5321' },
  rfc3463: { title: 'RFC 3463 — Enhanced Mail System Status Codes', url: 'https://www.rfc-editor.org/rfc/rfc3463' },
  rfc2142: { title: 'RFC 2142 — Mailbox Names for Common Services and Functions', url: 'https://www.rfc-editor.org/rfc/rfc2142' },
  rfc5322: { title: 'RFC 5322 — Internet Message Format', url: 'https://www.rfc-editor.org/rfc/rfc5322' },
  rfc8058: { title: 'RFC 8058 — One-Click Unsubscribe', url: 'https://www.rfc-editor.org/rfc/rfc8058' },
  googleSender: { title: 'Google — Email sender guidelines', url: 'https://support.google.com/a/answer/81126' },
  googlePostmaster: { title: 'Google Postmaster Tools', url: 'https://postmaster.google.com/' },
  spamhausBlocklists: { title: 'Spamhaus — Blocklist documentation', url: 'https://www.spamhaus.org/blocklists/' },
  spamhausRemoval: { title: 'Spamhaus — Blocklist Removal Center', url: 'https://check.spamhaus.org/' },
  microsoftSnds: { title: 'Microsoft — Smart Network Data Services', url: 'https://sendersupport.olc.protection.outlook.com/snds/' },
  e164: { title: 'ITU-T E.164 — International public telecommunication numbering plan', url: 'https://www.itu.int/rec/T-REC-E.164' },
} as const;

export const LEARN_ARTICLES: readonly LearnArticleMeta[] = [
  // ── Cluster A — Email authentication ───────────────────────────────────────
  {
    slug: 'email-authentication',
    title: 'How SPF, DKIM and DMARC work together',
    term: 'Email authentication',
    description:
      'SPF, DKIM and DMARC each prove something different, and DMARC is the only one receivers act on. How the three fit, in the order they run.',
    blurb:
      'The three records in one picture — what each proves, which one receivers actually enforce, and what Gmail and Yahoo now require.',
    cluster: 'authentication',
    pillar: true,
    updated: '2026-08-04',
    tool: { to: '/tools/domain-checker', label: 'Score a domain 0–100' },
    feature: { to: '/features/domain-health', label: 'Monitor every sending domain' },
    citations: [SOURCE.rfc7208, SOURCE.rfc6376, SOURCE.rfc7489, SOURCE.googleSender],
  },
  {
    slug: 'dkim-selector',
    title: 'What is a DKIM selector?',
    term: 'DKIM selector',
    description:
      'A DKIM selector tells a receiving server which public key to fetch. Where it lives, how to find yours, and the selectors every major provider publishes.',
    blurb:
      'Where the selector lives, how to read yours off a sent message, and a provider-by-provider table of the selectors our checker probes.',
    cluster: 'authentication',
    updated: '2026-08-04',
    tool: { to: '/tools/dkim-checker', label: 'Check a domain’s DKIM records' },
    citations: [SOURCE.rfc6376],
  },
  {
    slug: 'spf-record',
    title: 'What is an SPF record?',
    term: 'SPF record',
    description:
      'An SPF record lists the servers allowed to send mail using your domain. Why exactly one is legal, what each mechanism does, and what breaks it.',
    blurb:
      'One record, one line, and a surprising number of ways to break it. Mechanisms, qualifiers, and why a second record fails the whole thing.',
    cluster: 'authentication',
    updated: '2026-08-04',
    tool: { to: '/tools/spf-checker', label: 'Check a domain’s SPF record' },
    alsoServes: ['/tools/spf-generator'],
    citations: [SOURCE.rfc7208],
  },
  {
    slug: 'spf-too-many-dns-lookups',
    title: 'SPF: too many DNS lookups',
    term: 'SPF 10-lookup limit',
    description:
      'SPF allows 10 DNS lookups. Past that the record returns permerror and every receiver treats it as no SPF at all. How to count yours and get under.',
    blurb:
      'The limit that silently voids your SPF record — with the lookup cost of each major provider’s include, which nobody else publishes.',
    cluster: 'authentication',
    updated: '2026-08-04',
    tool: { to: '/tools/spf-checker', label: 'Check your SPF record' },
    citations: [SOURCE.rfc7208],
  },
  {
    slug: 'dkim',
    title: 'What is DKIM?',
    term: 'DKIM',
    description:
      'DKIM signs your mail so a receiver can prove it came from your domain unaltered. What it signs, why it survives forwarding, and what a fail costs.',
    blurb:
      'What the signature actually covers, why it survives forwarding when SPF does not, and the key-length decision that matters.',
    cluster: 'authentication',
    updated: '2026-08-04',
    tool: { to: '/tools/dkim-generator', label: 'Generate a 2048-bit DKIM key' },
    alsoServes: ['/tools/dkim-checker'],
    citations: [SOURCE.rfc6376],
  },
  {
    slug: 'dmarc',
    title: 'What is DMARC?',
    term: 'DMARC',
    description:
      'DMARC tells receivers what to do when SPF and DKIM fail to align. The none → quarantine → reject ramp, the pct tag, and how to move without losing mail.',
    blurb:
      'The policy layer that turns SPF and DKIM into something receivers act on — and how to ramp to reject without breaking your own mail.',
    cluster: 'authentication',
    updated: '2026-08-04',
    tool: { to: '/tools/dmarc-generator', label: 'Build a DMARC record' },
    alsoServes: ['/tools/dmarc-checker'],
    citations: [SOURCE.rfc7489],
  },
  {
    slug: 'dmarc-alignment',
    title: 'SPF passes but DMARC fails',
    term: 'DMARC alignment',
    description:
      'DMARC needs SPF or DKIM to pass AND align with your From: domain. Why a passing SPF check still fails DMARC, and how to fix each case.',
    blurb:
      'The single most confusing failure in email auth. Relaxed vs strict, envelope vs header, and why forwarding breaks SPF but not DKIM.',
    cluster: 'authentication',
    updated: '2026-08-04',
    tool: { to: '/tools/dmarc-checker', label: 'Read a domain’s DMARC policy' },
    citations: [SOURCE.rfc7489, SOURCE.rfc7208, SOURCE.rfc6376],
  },


  // ── Cluster B — Deliverability and reputation ──────────────────────────────
  {
    slug: 'why-email-goes-to-spam',
    title: 'Why does my email go to spam?',
    term: 'Spam folder placement',
    description:
      'Five causes of spam placement — authentication, reputation, list quality, engagement and content — ordered by how often each is the real one.',
    blurb:
      'Five causes, ordered by how often each is actually to blame. Start at the top; most people start at the bottom and rewrite their copy.',
    cluster: 'deliverability',
    pillar: true,
    updated: '2026-08-04',
    tool: { to: '/tools/email-spam-checker', label: 'Score an email 0–100' },
    feature: { to: '/features/inbox-placement', label: 'Test placement at real inboxes' },
    citations: [SOURCE.googleSender, SOURCE.rfc8058],
  },
  {
    slug: 'email-blacklist',
    title: 'What is an email blacklist?',
    term: 'Email blacklist',
    description:
      'DNSBLs list IPs and domains suspected of sending spam. Which lists actually affect delivery, which are noise, and what to do about a listing.',
    blurb:
      'Which of the public lists genuinely affect delivery and which you can safely ignore — they are not equal, and most guides pretend they are.',
    cluster: 'deliverability',
    updated: '2026-08-04',
    tool: { to: '/tools/blacklist-checker', label: 'Check an IP or domain' },
    feature: { to: '/features/blacklist-checks', label: 'Monitor listings daily' },
    citations: [SOURCE.spamhausBlocklists],
  },
  {
    slug: 'spamhaus-listing',
    title: 'How to get delisted from Spamhaus',
    term: 'Spamhaus listing',
    description:
      'SBL, CSS, XBL, PBL and DBL are separate lists with different causes and different remedies. Identify which one has you, then fix that one.',
    blurb:
      'Five lists, five completely different remedies. Removal requests fail mostly because people request removal from the wrong one.',
    cluster: 'deliverability',
    updated: '2026-08-04',
    tool: { to: '/tools/blacklist-checker', label: 'Check your listing status' },
    citations: [SOURCE.spamhausBlocklists, SOURCE.spamhausRemoval],
  },
  {
    slug: 'sender-reputation',
    title: 'What is sender reputation?',
    term: 'Sender reputation',
    description:
      'Mailbox providers score your domain and your IP separately, from engagement and complaints. What feeds each, and which one you actually control.',
    blurb:
      'Domain reputation and IP reputation are different scores with different owners — and on a shared IP only one of them is yours to fix.',
    cluster: 'deliverability',
    updated: '2026-08-04',
    tool: { to: '/tools/domain-checker', label: 'Check your domain posture' },
    feature: { to: '/features/warmup', label: 'Warm a cold sending domain' },
    citations: [SOURCE.googlePostmaster, SOURCE.microsoftSnds, SOURCE.googleSender],
  },
  {
    slug: 'smtp-error-codes',
    title: 'SMTP error codes, decoded',
    term: 'SMTP error code',
    description:
      'What 550 5.7.1, 421 4.7.0, 550 5.1.1 and the rest actually mean at Gmail, Outlook and Yahoo — and the specific fix for each one.',
    blurb:
      'A reference table of the codes the big providers actually emit, what each is really telling you, and the fix per code.',
    cluster: 'deliverability',
    updated: '2026-08-04',
    tool: { to: '/tools/smtp-test', label: 'Run a live SMTP transcript' },
    citations: [SOURCE.rfc5321, SOURCE.rfc3463],
  },
  {
    slug: 'email-warmup',
    title: 'What is email warmup, and does it work?',
    term: 'Email warmup',
    description:
      'Warmup raises sending volume gradually so providers learn your domain. Realistic ramp schedules, what it fixes, and what it cannot fix.',
    blurb:
      'Realistic ramp schedules, plus an honest account of the cases where warmup does nothing at all and something else is wrong.',
    cluster: 'deliverability',
    updated: '2026-08-04',
    tool: { to: '/tools/domain-checker', label: 'Check the domain first' },
    feature: { to: '/features/warmup', label: 'Automated mailbox warmup' },
    citations: [SOURCE.googlePostmaster, SOURCE.googleSender],
  },

  // ── Cluster C — List quality and signup integrity ──────────────────────────
  {
    slug: 'email-bounce-rate',
    title: 'What is a good email bounce rate?',
    term: 'Email bounce rate',
    description:
      'Hard bounces above 2% put your sending at risk. What each provider acts on, why hard and soft bounces are different problems, and how to get under.',
    blurb:
      'The thresholds providers actually act on, why hard and soft are different problems, and where the 2% rule of thumb comes from.',
    cluster: 'integrity',
    pillar: true,
    updated: '2026-08-04',
    tool: { to: '/tools/domain-checker', label: 'Check a sending domain' },
    citations: [SOURCE.rfc3463, SOURCE.googleSender],
  },
  {
    slug: 'catch-all-email',
    title: 'What is a catch-all email address?',
    term: 'Catch-all domain',
    description:
      'A catch-all domain accepts mail for every address, so verification cannot prove a mailbox exists. Why you get "unknown", and what to do with those.',
    blurb:
      'Why every verifier returns "unknown" on these, what that means for your list, and how to decide whether to send anyway.',
    cluster: 'integrity',
    updated: '2026-08-04',
    tool: { to: '/tools/smtp-test', label: 'Probe a mail server directly' },
    citations: [SOURCE.rfc5321],
  },
  {
    slug: 'disposable-email-addresses',
    title: 'Disposable email addresses',
    term: 'Disposable email address',
    description:
      'Throwaway inboxes used to pass signup gates. Why domain blocklists decay within weeks, and what to detect instead of maintaining a list.',
    blurb:
      'Why domain blocklists decay within weeks of publication, and the structural signals that keep working after the list goes stale.',
    cluster: 'integrity',
    updated: '2026-08-04',
    tool: { to: '/tools/domain-checker', label: 'Inspect a domain' },
    feature: { to: '/features/bot-detection', label: 'Score signups in real time' },
    citations: [SOURCE.rfc5321],
  },
  {
    slug: 'multi-accounting',
    title: 'What is multi-accounting?',
    term: 'Multi-accounting',
    description:
      'One person, many accounts, one mailbox. How Gmail dot-folding and plus-addressing make it trivial, and how canonicalisation collapses the variants.',
    blurb:
      'Gmail dot-folding and plus-addressing worked through — including the canonicalisation we run on every address in production.',
    cluster: 'integrity',
    updated: '2026-08-04',
    tool: { to: '/tools/domain-checker', label: 'Check a domain' },
    feature: { to: '/features/bot-detection', label: 'Cluster near-duplicate signups' },
    citations: [SOURCE.rfc5321],
  },
  {
    slug: 'role-based-email-addresses',
    title: 'Role-based email addresses',
    term: 'Role-based email address',
    description:
      'info@, sales@, support@ — shared mailboxes with no single owner. Why they raise complaint rates, and when blocking them costs more than it saves.',
    blurb:
      'Why shared mailboxes drive complaints, and the cases where blocking them at signup costs more revenue than it protects.',
    cluster: 'integrity',
    updated: '2026-08-04',
    tool: { to: '/tools/domain-checker', label: 'Check a domain' },
    citations: [SOURCE.rfc2142],
  },
  {
    slug: 'voip-vs-mobile-numbers',
    title: 'VoIP vs mobile numbers',
    term: 'VoIP number',
    description:
      'Line-type lookup tells you whether a number is mobile, landline or VoIP. Why VoIP correlates with fraud without proving it, and how to use that.',
    blurb:
      'What line-type lookup can and cannot tell you, and why treating VoIP as fraud blocks a lot of legitimate customers.',
    cluster: 'integrity',
    updated: '2026-08-04',
    tool: { to: '/tools/domain-checker', label: 'Check a domain' },
    feature: { to: '/features/phone-validation', label: 'Validate numbers at signup' },
    citations: [SOURCE.e164],
  },

  // ── Cluster D — Copy and content ───────────────────────────────────────────
  {
    slug: 'spam-trigger-words',
    title: 'Do spam trigger words still matter?',
    term: 'Spam trigger word',
    description:
      'Modern filters weigh sender reputation far above vocabulary. What word lists still catch, what they never caught, and how much to actually care.',
    blurb:
      'An honest answer: filters weigh reputation far above vocabulary, and most published word lists trace back to a single list from the 2000s.',
    cluster: 'copy',
    pillar: true,
    updated: '2026-08-04',
    tool: { to: '/tools/spam-words-checker', label: 'Scan a subject and body' },
    alsoServes: ['/tools/email-spam-checker'],
    citations: [SOURCE.googleSender, SOURCE.rfc8058],
  },
  {
    slug: 'email-subject-line-length',
    title: 'The right email subject line length',
    term: 'Subject line length',
    description:
      'Gmail on mobile shows about 35 characters, desktop about 70, Outlook about 60. Where each client truncates, and what to put before the cut.',
    blurb:
      'Where each client truncates, and the rule that follows: front-load the specific, because the tail may never be read at all.',
    cluster: 'copy',
    updated: '2026-08-04',
    tool: { to: '/tools/subject-line-tester', label: 'Score a subject line' },
    alsoServes: ['/tools/subject-line-generator'],
    citations: [SOURCE.rfc5322],
  },
  {
    slug: 'email-open-rate',
    title: 'Why email open rates stopped being reliable',
    term: 'Email open rate',
    description:
      'Apple Mail Privacy Protection pre-loads tracking pixels, so a large share of your opens never happened. What broke, and what to measure instead.',
    blurb:
      'Apple MPP fires the tracking pixel whether or not anyone read the message. What that did to the number, and the metrics that survived.',
    cluster: 'copy',
    updated: '2026-08-04',
    tool: { to: '/tools/subject-line-generator', label: 'Generate subject variants' },
    alsoServes: ['/tools/ai-email-writer'],
    citations: [SOURCE.googlePostmaster],
  },
];

/** Look an article up by slug. Throws rather than returning undefined — a typo'd slug is a bug. */
export function learnArticle(slug: string): LearnArticleMeta {
  const found = LEARN_ARTICLES.find((a) => a.slug === slug);
  if (!found) throw new Error(`No /learn article registered for slug "${slug}"`);
  return found;
}

/**
 * The pillar of a cluster. Every cluster that has articles has exactly one — asserted by
 * learn-clusters.spec.ts. Clusters are published one at a time (the derived link matrix means a
 * half-built cluster would link to pages that do not exist), so a planned-but-unbuilt cluster
 * legitimately has none yet and `maybePillarOf` is the tolerant form used for cross-links.
 */
export function pillarOf(cluster: ClusterKey): LearnArticleMeta {
  const found = maybePillarOf(cluster);
  if (!found) throw new Error(`Cluster "${cluster}" has no pillar article`);
  return found;
}

export function maybePillarOf(cluster: ClusterKey): LearnArticleMeta | undefined {
  return LEARN_ARTICLES.find((a) => a.cluster === cluster && a.pillar);
}

/** Clusters that actually have articles, in declared order. Drives the index page grouping. */
export function publishedClusters(): LearnCluster[] {
  return LEARN_CLUSTERS.filter((c) => LEARN_ARTICLES.some((a) => a.cluster === c.key));
}

/** Every article in a cluster except the pillar, in registry order. */
export function spokesOf(cluster: ClusterKey): LearnArticleMeta[] {
  return LEARN_ARTICLES.filter((a) => a.cluster === cluster && !a.pillar);
}

export function clusterOf(key: ClusterKey): LearnCluster {
  const found = LEARN_CLUSTERS.find((c) => c.key === key);
  if (!found) throw new Error(`No cluster registered for key "${key}"`);
  return found;
}

/**
 * The link matrix for one article, derived rather than hand-written.
 *
 * - a SPOKE links up to its pillar, then across to its 3 nearest siblings (registry order,
 *   wrapping), then out to one article in another cluster.
 * - a PILLAR links down to EVERY spoke in its cluster — that is what makes it a hub rather than
 *   just a long article, and it is the half of the matrix that decays first when done by hand.
 */
export function relatedFor(slug: string): LearnArticleMeta[] {
  const self = learnArticle(slug);
  if (self.pillar) return spokesOf(self.cluster);

  const pillar = pillarOf(self.cluster);
  const siblings = spokesOf(self.cluster).filter((a) => a.slug !== slug);
  const start = siblings.findIndex((a) => LEARN_ARTICLES.indexOf(a) > LEARN_ARTICLES.indexOf(self));
  const from = start === -1 ? 0 : start;
  const ordered = [...siblings.slice(from), ...siblings.slice(0, from)].slice(0, 3);

  // One cross-cluster link. The skill's matrix allows 0-1; taking the next cluster's pillar keeps
  // the whole section reachable from any article in at most two hops.
  const published = publishedClusters();
  const idx = published.findIndex((c) => c.key === self.cluster);
  const next = published[(idx + 1) % published.length];
  const bridgePillar = next && next.key !== self.cluster ? maybePillarOf(next.key) : undefined;

  return [pillar, ...ordered, ...(bridgePillar ? [bridgePillar] : [])];
}

/**
 * THE REVERSE EDGE — which articles a given `/tools/*` or `/features/*` page should link to.
 *
 * Derived by inverting the `tool` / `feature` / `alsoServes` declarations above, so the pairing is
 * stated exactly once and both directions follow from it. Before this existed the linking was
 * one-way: 20 of the 21 tool and feature pages had ZERO in-body links into `/learn/`, which left a
 * 13-article section depending on the footer and its own internal rails for every inbound link —
 * while the pages that actually draw impressions passed it nothing.
 *
 * Returns [] for pages with no genuine match (the AI writing tools, and the features whose
 * articles land with clusters C and D). An empty result renders nothing; it does not render a
 * weaker link. Those fill in automatically as the remaining clusters ship.
 *
 * Ordered by how directly the article explains that page: primary CTA pairings first.
 */
export function learnFor(path: string): LearnArticleMeta[] {
  const bare = path.replace(/\/+$/, '');
  const primary = LEARN_ARTICLES.filter((a) => a.tool.to === bare || a.feature?.to === bare);
  const secondary = LEARN_ARTICLES.filter(
    (a) => !primary.includes(a) && a.alsoServes?.includes(bare),
  );
  return [...primary, ...secondary];
}
